export type Language = 'es' | 'ca' | 'en'

export const translations = {
  es: {
    nav: {
      about: 'Nosotros',
      menu: 'Carta',
      pool: 'Piscina & Lounge',
      reservations: 'Reservas',
      gallery: 'Galería',
      contact: 'Contacto',
      bookNow: 'Reservar Mesa',
      home: 'Inicio',
    },
    hero: {
      tagline: 'Tradición mediterránea frente al mar',
      book: 'Reservar Mesa',
      menu: 'Ver Carta',
    },
    home: {
      discoverMore: 'Descubrir Más',
      featuredTitle: 'Platos Destacados',
      featuredSubtitle: 'Selección del Chef',
      viewFullMenu: 'Ver Carta Completa',
      ctaTitle: 'Vive la Experiencia',
      ctaSubtitle:
        'Reserva tu mesa y disfruta de la auténtica gastronomía mediterránea frente al mar.',
      viewGallery: 'Ver Galería Completa',
    },
    about: {
      title: 'Nuestra Historia',
      subtitle: 'Sabor a mar y tradición',
      description1:
        "Nuestra historia comenzó el 2 de octubre de 2019, cuando abrimos nuestras puertas por primera vez en Sant Jordi d'Alfama con la ilusión de compartir nuestra pasión por la gastronomía marinera.",
      description2:
        'Sin embargo, el camino no fue fácil. Poco después de abrir, un incendio en la cocina nos obligó a cerrar. Tras mucho esfuerzo, logramos reabrir durante un mes, justo antes de que la pandemia global nos forzara a cerrar de nuevo. Hoy, más fuertes que nunca, seguimos ofreciendo la esencia del Mediterráneo en cada plato.',
      timelineTitle: 'Un Camino de Resiliencia',
      timeline: [
        {
          year: '2019',
          title: 'El Comienzo',
          description:
            "El 2 de octubre abrimos nuestras puertas por primera vez en Sant Jordi d'Alfama, llenos de ilusión y ganas de compartir nuestra cocina.",
        },
        {
          year: '2019',
          title: 'El Incendio',
          description:
            'Poco después de la inauguración, un pequeño incendio en la cocina dañó nuestras instalaciones, impidiéndonos operar y obligándonos a reconstruir lo que acabábamos de crear.',
        },
        {
          year: '2020',
          title: 'Reapertura y Pandemia',
          description:
            'Tras las reparaciones, logramos reabrir y operamos durante aproximadamente un mes. Sin embargo, en marzo de 2020, la pandemia de COVID-19 nos obligó a cerrar de nuevo.',
        },
        {
          year: 'Hoy',
          title: 'Más Fuertes que Nunca',
          description:
            'Perseveramos a través de las adversidades. Hoy estamos abiertos, más fuertes que nunca, ofreciendo la auténtica experiencia mediterránea frente al mar.',
        },
      ],
      philosophyTitle: 'Nuestra Filosofía',
      philosophySubtitle: 'Nuestros Pilares',
      phil1Title: 'Ingredientes Frescos',
      phil1Desc:
        'Seleccionamos el mejor producto de proximidad diariamente en las lonjas locales.',
      phil2Title: 'Tradición Mediterránea',
      phil2Desc:
        'Respetamos las recetas clásicas aportando nuestro toque personal y contemporáneo.',
      phil3Title: 'Hospitalidad Cálida',
      phil3Desc:
        'Te hacemos sentir como en casa con un servicio atento, profesional y cercano.',
      teamTitle: 'El Equipo',
      teamSubtitle: 'Pasión por la cocina',
      teamDesc:
        'Detrás de cada plato hay un equipo de profesionales apasionados por la gastronomía. Liderados por nuestro Chef Ejecutivo, trabajamos cada día para ofrecerte la mejor experiencia culinaria, combinando técnica, creatividad y un profundo respeto por el producto.',
    },
    menu: {
      title: 'Nuestra Carta',
      subtitle: 'Sabores auténticos',
      intro:
        'Descubre nuestra selección de platos elaborados con los mejores ingredientes de temporada. Una propuesta gastronómica que rinde homenaje a los sabores del Mediterráneo.',
      pricePerUnit: 'Precio / unidad',
      categories: {
        starters: 'Entrantes',
        fish: 'Pescados',
        cooked: 'Cocinados',
        meat: 'Carnes',
        rice: 'Arroces (min. 2 pax)',
        kids: 'Niños',
        desserts: 'Postres',
        extras: 'Aperitivo y Extras',
        dinnerStarters: 'Cenas - Entrantes',
        dinnerGrill: 'Cenas - Brasa',
        dinnerVermut: 'Cenas - Vermut',
      },
      items: {
        starters: [
          { num: '1', name: 'Ensalada Mediterránea con Atún', price: '11,50€' },
          {
            num: '2',
            name: 'Ensalada de Tomate de Montserrat',
            desc: 'Cebolla, aceitunas de Kalamata y Boquerones',
            price: '16,50€',
          },
          {
            num: '3',
            name: 'Alcachofas de Temporada a la Brasa',
            price: '5,00€',
            pricePerUnit: true,
          },
          {
            num: '4',
            name: 'Corazón de Alcachofas Confitadas',
            desc: 'Con jamón de bellota',
            price: '17,50€',
          },
          { num: '5', name: 'Mejillones al Estilo del Chef', price: '14,50€' },
          { num: '6', name: 'Mejillones a la Marinera', price: '16,50€' },
          { num: '7', name: 'Tallarinas', price: '21,00€' },
          { num: '8', name: 'Calamares a la Romana', price: '18,50€' },
          { num: '9', name: 'Fritada de Pescado', price: '18,50€' },
          {
            num: '10',
            name: 'Carpaccio de Pulpo de Roca',
            desc: 'Estilo Gallego',
            price: '21,00€',
            recommended: true,
          },
          {
            num: '11',
            name: 'Carpaccio de Gamba Roja',
            desc: 'Con mayonesa de wasabi y brotes frescos',
            price: '21,00€',
            recommended: true,
          },
          { num: '12', name: 'Croquetas de Setas', price: '16,50€' },
          {
            num: '13',
            name: 'Croquetón de Gambas',
            desc: 'Con mayonesa de Trufa',
            price: '16,50€',
          },
          {
            num: '14',
            name: 'Croquetas de Cochinillo',
            desc: 'Con Corazón de Manzana',
            price: '16,50€',
          },
        ],
        fish: [
          {
            num: '77',
            name: 'Gamba Roja',
            price: '5,00€',
            pricePerUnit: true,
            recommended: true,
          },
          {
            num: '15',
            name: 'Cigalas 00',
            price: '13,50€',
            pricePerUnit: true,
            recommended: true,
          },
          {
            num: '16',
            name: 'Langostinos de Lonja',
            desc: '8 unidades',
            price: '28,00€',
          },
          { num: '17', name: 'Dorada a la Plancha', price: '21,00€' },
          {
            num: '18',
            name: 'Lenguado de Lonja a la Plancha',
            price: '28,00€',
          },
          {
            num: '19',
            name: 'Rape de Profundidad a la Plancha',
            price: '28,00€',
          },
          { num: '20', name: 'Suquet de Rape', price: '31,00€' },
          {
            num: '21',
            name: 'Calamares de Costa a la Plancha',
            price: '21,00€',
          },
          {
            num: '22',
            name: 'Lomo de Bacalao Gratinado',
            desc: 'Con Muselina de Ajo',
            price: '26,00€',
          },
          {
            num: '23',
            name: 'Pata de Pulpo Mediterráneo 250g',
            desc: 'A la Brasa con Alioli y Patata Confitada',
            price: '22,00€',
          },
          {
            num: '24',
            name: 'Lomo de Atún Balfegó a la Plancha',
            price: '28,00€',
            recommended: true,
          },
          {
            num: '25',
            name: 'Tataki de Atún BALFEGÓ',
            price: '26,00€',
            recommended: true,
          },
          {
            num: '26',
            name: 'Sashimi de Atún BALFEGÓ',
            price: '26,00€',
            recommended: true,
          },
          {
            num: '27',
            name: 'Rodaballo de Lonja',
            desc: 'Para compartir, acompañado de verduritas',
            price: '86,00€',
            recommended: true,
          },
          {
            num: '28',
            name: 'Lata de Caviar 10g',
            desc: 'Con pan de Alga y mantequilla',
            price: '38,00€',
            recommended: true,
          },
          {
            num: '29',
            name: 'Espardeñas 150g',
            price: '60,00€',
            recommended: true,
          },
        ],
        cooked: [
          {
            num: '205',
            name: 'Costilla de Vacuno a Baja Temperatura',
            desc: 'Con mostaza',
            price: '21,00€',
          },
          {
            num: '206',
            name: 'Costillas y Medianas de Cabrito',
            desc: 'De Horta de Sant Joan, a la Brasa',
            price: '26,00€',
          },
        ],
        meat: [
          { num: '24', name: 'Entrecot Madurado Iruki', price: '26,00€' },
          {
            num: '25',
            name: 'Solomillo de Ternera Iruki',
            price: '28,00€',
            recommended: true,
          },
          {
            num: '26',
            name: 'Secreto Ibérico al Teriyaki',
            desc: 'Con patatas paja',
            price: '21,00€',
          },
          { num: '27', name: 'Pluma Ibérica a la Plancha', price: '26,00€' },
          {
            num: '28',
            name: 'Entraña de Cerdo Ibérico',
            desc: 'A la brasa o crema Astillero',
            price: '26,00€',
          },
          {
            num: '29',
            name: 'Chuletón Maduro 500g',
            desc: 'Con patatas paja y verduras',
            price: '38,00€',
            recommended: true,
          },
        ],
        rice: [
          { num: '97', name: 'Arroz Marinera con Gamba Roja', price: '21,00€' },
          { num: '98', name: 'Arroz Negro', price: '21,00€' },
          {
            num: '99',
            name: 'Arroz Caldoso con Bogavante',
            desc: 'Gratinado con alioli (Nacional y vivo)',
            price: '33,00€',
            recommended: true,
          },
          { num: '100', name: 'Fideuà', price: '19,50€' },
        ],
        kids: [
          {
            num: '18',
            name: 'Pasta Fresca',
            desc: 'Carbonara o boloñesa',
            price: '13,50€',
          },
          { num: '19', name: 'Canelones', price: '10,50€' },
          { num: '20', name: 'Bandidos con Patatas', price: '13,50€' },
          { num: '21', name: 'Butifarra con Patatas', price: '13,50€' },
          {
            num: '22',
            name: 'Croquetas de Jamón con Patatas',
            price: '12,50€',
          },
        ],
        desserts: [
          { num: '106', name: 'Cardenal', price: '10,50€' },
          { num: '107', name: 'Tarta de Queso', price: '8,80€' },
          {
            num: '108',
            name: 'Torrijas',
            desc: 'Con Chupito de Arroz y Helado de Vainilla',
            price: '8,80€',
          },
          {
            num: '109',
            name: 'Hojaldre de Piñones',
            desc: 'Con crema catalana',
            price: '8,80€',
          },
          {
            num: '110',
            name: 'Brookies',
            desc: 'Con helado de vainilla',
            price: '8,80€',
          },
          {
            num: '111',
            name: 'Coulant de Chocolate',
            desc: 'Con Helado de Vainilla',
            price: '7,00€',
          },
          { num: '112', name: 'Trufas Heladas con Nata', price: '7,50€' },
          { num: '113', name: 'Fresas con Nata', price: '8,80€' },
          {
            num: '114',
            name: 'Sorbete de Mandarina',
            desc: 'Con Coulis de frutas del bosque',
            price: '8,50€',
          },
          {
            num: '115',
            name: 'Mosaico de Frutas del Tiempo',
            desc: 'Con chocolate caliente',
            price: '12,50€',
          },
        ],
        extras: [
          { num: '123', name: 'Pan', price: '1,30€' },
          { num: '124', name: 'Salsas', price: '2,50€' },
          { num: '125', name: 'Pan con Tomate', price: '2,00€' },
        ],
        dinnerStarters: [
          {
            num: '210',
            name: 'Ensalada de Tomate y Burrata',
            desc: 'Con gamba y trufa',
            price: '16,50€',
          },
          {
            num: '211',
            name: 'Ensalada de Brotes Frescos',
            desc: 'Con tartar de Atún',
            price: '16,50€',
          },
          {
            num: '212',
            name: 'Carpaccio de Gamba Roja',
            desc: 'Con mayonesa de wasabi y brotes frescos',
            price: '21,00€',
          },
          {
            num: '213',
            name: 'Carpaccio de Pulpo de Roca',
            desc: 'Estilo Gallego',
            price: '21,00€',
          },
          {
            num: '214',
            name: 'Jamón 100% Bellota 100g',
            desc: 'Con Pan y Tomate',
            price: '26,00€',
          },
          {
            num: '215',
            name: 'Tabla de Quesos',
            desc: 'Con Mermelada y Frutos Secos',
            price: '16,50€',
          },
        ],
        dinnerGrill: [
          {
            num: '4',
            name: 'Alcachofas de Temporada a la Brasa',
            price: '5,00€',
            pricePerUnit: true,
          },
          {
            num: '27',
            name: 'Rodaballo de Lonja',
            desc: 'Para compartir, acompañado de verduritas',
            price: '86,00€',
          },
          {
            num: '216',
            name: 'Lomo Alto de Bacalao a la Brasa',
            desc: 'Con verduras',
            price: '28,00€',
            pricePerUnit: true,
          },
          {
            num: '29',
            name: 'Chuletón Maduro 500g',
            desc: 'Con patatas paja y verduras',
            price: '38,00€',
          },
          {
            num: '217',
            name: 'Galta al Horno',
            desc: 'Estilo el Astillero',
            price: '17,50€',
          },
          {
            num: '28',
            name: 'Entraña de Cerdo Ibérico',
            desc: 'A la brasa o crema Astillero',
            price: '26,00€',
          },
          { num: '124', name: 'Salsas', price: '2,50€' },
          { num: '125', name: 'Pan con Tomate', price: '2,00€' },
        ],
        dinnerVermut: [
          { num: '122', name: 'Aceitunas', price: '2,00€' },
          { num: '218', name: 'Bolsa Patatas Chips', price: '2,00€' },
          { num: '219', name: 'Patatas Fritas', price: '5,00€' },
        ],
      },
    },
    reservations: {
      title: 'Haz tu Reserva',
      subtitle: 'Te esperamos',
      form: {
        name: 'Nombre completo',
        email: 'Correo electrónico',
        phone: 'Teléfono',
        date: 'Fecha',
        time: 'Hora',
        guests: 'Número de personas',
        requests: 'Peticiones especiales (opcional)',
        submit: 'Confirmar Reserva',
        successTitle: '¡Reserva solicitada!',
        successMessage:
          'Hemos recibido tu solicitud. Te contactaremos pronto para confirmar la reserva.',
        newReservation: 'Hacer otra reserva',
      },
    },
    gallery: {
      title: 'Galería',
      subtitle: 'Nuestra esencia en imágenes',
    },
    location: {
      title: 'Ubicación y Contacto',
      subtitle: 'Dónde encontrarnos',
      address: 'Dirección',
      phone: 'Teléfono',
      email: 'Email',
      hours: 'Horario',
      hoursText: 'Mar - Dom: 13:00 - 16:00, 20:00 - 23:00\nLunes: Cerrado',
      contactFormTitle: 'Envíanos un Mensaje',
      contactFormSubtitle: '¿Tienes alguna duda?',
      formName: 'Nombre',
      formEmail: 'Email',
      formMessage: 'Mensaje',
      formSubmit: 'Enviar Mensaje',
      formSuccess:
        'Mensaje enviado correctamente. Te responderemos lo antes posible.',
    },
    pool: {
      title: 'Piscina & Lounge',
      subtitle: 'Relax mediterráneo',
      description1:
        'Disfruta de nuestra exclusiva piscina rodeada de un entorno natural privilegiado. El complemento perfecto para una experiencia gastronómica inolvidable.',
      description2:
        'Relájate antes o después de tu comida en un ambiente que combina frescura, elegancia y las mejores vistas del Mediterráneo. Un espacio pensado para el disfrute y el descanso.',
      featureTitle1: 'Piscina Privada',
      featureDesc1:
        'Refréscate en nuestra piscina con vistas panorámicas al entorno natural.',
      featureTitle2: 'Terraza Lounge',
      featureDesc2:
        'Disfruta de cócteles y aperitivos en nuestra terraza junto a la piscina.',
      featureTitle3: 'Atardecer Único',
      featureDesc3:
        'Contempla los mejores atardeceres desde nuestras instalaciones.',
      galleryTitle: 'Descubre el Espacio',
      cta: 'Reservar Experiencia',
    },
    footer: {
      rights: 'Todos los derechos reservados.',
      legal: 'Aviso Legal',
      privacy: 'Política de Privacidad',
    },
  },
  ca: {
    nav: {
      about: 'Nosaltres',
      menu: 'Carta',
      pool: 'Piscina & Lounge',
      reservations: 'Reserves',
      gallery: 'Galeria',
      contact: 'Contacte',
      bookNow: 'Reservar Taula',
      home: 'Inici',
    },
    hero: {
      tagline: 'Tradició mediterrània davant del mar',
      book: 'Reservar Taula',
      menu: 'Veure Carta',
    },
    home: {
      discoverMore: 'Descobrir Més',
      featuredTitle: 'Plats Destacats',
      featuredSubtitle: 'Selecció del Xef',
      viewFullMenu: 'Veure Carta Completa',
      ctaTitle: "Viu l'Experiència",
      ctaSubtitle:
        "Reserva la teva taula i gaudeix de l'autèntica gastronomia mediterrània davant del mar.",
      viewGallery: 'Veure Galeria Completa',
    },
    about: {
      title: 'La Nostra Història',
      subtitle: 'Sabor a mar i tradició',
      description1:
        "La nostra història va començar el 2 d'octubre de 2019, quan vam obrir les nostres portes per primera vegada a Sant Jordi d'Alfama amb la il·lusió de compartir la nostra passió per la gastronomia marinera.",
      description2:
        "No obstant això, el camí no va ser fàcil. Poc després d'obrir, un incendi a la cuina ens va obligar a tancar. Després de molt d'esforç, vam aconseguir reobrir durant un mes, just abans que la pandèmia global ens forcés a tancar de nou. Avui, més forts que mai, continuem oferint l'essència del Mediterrani en cada plat.",
      timelineTitle: 'Un Camí de Resiliència',
      timeline: [
        {
          year: '2019',
          title: 'El Començament',
          description:
            "El 2 d'octubre vam obrir les nostres portes per primera vegada a Sant Jordi d'Alfama, plens d'il·lusió i ganes de compartir la nostra cuina.",
        },
        {
          year: '2019',
          title: "L'Incendi",
          description:
            'Poc després de la inauguració, un petit incendi a la cuina va danyar les nostres instal·lacions, impedint-nos operar i obligant-nos a reconstruir el que acabàvem de crear.',
        },
        {
          year: '2020',
          title: 'Reobertura i Pandèmia',
          description:
            'Després de les reparacions, vam aconseguir reobrir i vam operar durant aproximadament un mes. No obstant això, el març de 2020, la pandèmia de COVID-19 ens va obligar a tancar de nou.',
        },
        {
          year: 'Avui',
          title: 'Més Forts que Mai',
          description:
            "Vam perseverar a través de les adversitats. Avui estem oberts, més forts que mai, oferint l'autèntica experiència mediterrània davant del mar.",
        },
      ],
      philosophyTitle: 'La Nostra Filosofia',
      philosophySubtitle: 'Els Nostres Pilars',
      phil1Title: 'Ingredients Frescos',
      phil1Desc:
        'Seleccionem el millor producte de proximitat diàriament a les llotges locals.',
      phil2Title: 'Tradició Mediterrània',
      phil2Desc:
        'Respectem les receptes clàssiques aportant el nostre toc personal i contemporani.',
      phil3Title: 'Hospitalitat Càlida',
      phil3Desc:
        'Et fem sentir com a casa amb un servei atent, professional i proper.',
      teamTitle: "L'Equip",
      teamSubtitle: 'Passió per la cuina',
      teamDesc:
        'Darrere de cada plat hi ha un equip de professionals apassionats per la gastronomia. Liderats pel nostre Xef Executiu, treballem cada dia per oferir-te la millor experiència culinària, combinant tècnica, creativitat i un profund respecte pel producte.',
    },
    menu: {
      title: 'La Nostra Carta',
      subtitle: 'Sabors autèntics',
      intro:
        'Descobreix la nostra selecció de plats elaborats amb els millors ingredients de temporada. Una proposta gastronòmica que ret homenatge als sabors del Mediterrani.',
      pricePerUnit: 'Preu / unitat',
      categories: {
        starters: 'Entrants',
        fish: 'Peixos',
        cooked: 'Cuinats',
        meat: 'Carns',
        rice: 'Arrossos (min. 2 pax)',
        kids: 'Nens',
        desserts: 'Postres',
        extras: 'Aperitiu i Extres',
        dinnerStarters: 'Sopars - Entrants',
        dinnerGrill: 'Sopars - Brasa',
        dinnerVermut: 'Sopars - Vermut',
      },
      items: {
        starters: [
          { num: '1', name: 'Ensalada Mediterránea con Atún', price: '11,50€' },
          {
            num: '2',
            name: 'Ensalada de Tomate de Montserrat',
            desc: 'Ceba, olives de Kalamata i Seitons',
            price: '16,50€',
          },
          {
            num: '3',
            name: 'Alcachofas de Temporada a la Brasa',
            price: '5,00€',
            pricePerUnit: true,
          },
          {
            num: '4',
            name: 'Corazón de Alcachofas Confitadas',
            desc: 'Amb pernil de gla',
            price: '17,50€',
          },
          { num: '5', name: 'Mejillones al Estilo del Chef', price: '14,50€' },
          { num: '6', name: 'Mejillones a la Marinera', price: '16,50€' },
          { num: '7', name: 'Tallarinas', price: '21,00€' },
          { num: '8', name: 'Calamares a la Romana', price: '18,50€' },
          { num: '9', name: 'Fritada de Pescado', price: '18,50€' },
          {
            num: '10',
            name: 'Carpaccio de Pulpo de Roca',
            desc: 'Estil Gallec',
            price: '21,00€',
            recommended: true,
          },
          {
            num: '11',
            name: 'Carpaccio de Gamba Roja',
            desc: 'Amb maionesa de wasabi i brots frescos',
            price: '21,00€',
            recommended: true,
          },
          { num: '12', name: 'Croquetas de Setas', price: '16,50€' },
          {
            num: '13',
            name: 'Croquetón de Gambas',
            desc: 'Amb maionesa de Tòfona',
            price: '16,50€',
          },
          {
            num: '14',
            name: 'Croquetas de Cochinillo',
            desc: 'Amb Cor de Poma',
            price: '16,50€',
          },
        ],
        fish: [
          {
            num: '77',
            name: 'Gamba Roja',
            price: '5,00€',
            pricePerUnit: true,
            recommended: true,
          },
          {
            num: '15',
            name: 'Cigalas 00',
            price: '13,50€',
            pricePerUnit: true,
            recommended: true,
          },
          {
            num: '16',
            name: 'Langostinos de Lonja',
            desc: '8 unitats',
            price: '28,00€',
          },
          { num: '17', name: 'Dorada a la Plancha', price: '21,00€' },
          {
            num: '18',
            name: 'Lenguado de Lonja a la Plancha',
            price: '28,00€',
          },
          {
            num: '19',
            name: 'Rape de Profundidad a la Plancha',
            price: '28,00€',
          },
          { num: '20', name: 'Suquet de Rape', price: '31,00€' },
          {
            num: '21',
            name: 'Calamares de Costa a la Plancha',
            price: '21,00€',
          },
          {
            num: '22',
            name: 'Lomo de Bacalao Gratinado',
            desc: "Amb Mussolina d'All",
            price: '26,00€',
          },
          {
            num: '23',
            name: 'Pata de Pulpo Mediterráneo 250g',
            desc: 'A la Brasa amb Allioli i Patata Confitada',
            price: '22,00€',
          },
          {
            num: '24',
            name: 'Lomo de Atún Balfegó a la Plancha',
            price: '28,00€',
            recommended: true,
          },
          {
            num: '25',
            name: 'Tataki de Atún BALFEGÓ',
            price: '26,00€',
            recommended: true,
          },
          {
            num: '26',
            name: 'Sashimi de Atún BALFEGÓ',
            price: '26,00€',
            recommended: true,
          },
          {
            num: '27',
            name: 'Rodaballo de Lonja',
            desc: 'Per compartir, acompanyat de verduretes',
            price: '86,00€',
            recommended: true,
          },
          {
            num: '28',
            name: 'Lata de Caviar 10g',
            desc: "Amb pa d'Alga i mantega",
            price: '38,00€',
            recommended: true,
          },
          {
            num: '29',
            name: 'Espardeñas 150g',
            price: '60,00€',
            recommended: true,
          },
        ],
        cooked: [
          {
            num: '205',
            name: 'Costilla de Vacuno a Baja Temperatura',
            desc: 'Amb mostassa',
            price: '21,00€',
          },
          {
            num: '206',
            name: 'Costillas y Medianas de Cabrito',
            desc: "D'Horta de Sant Joan, a la Brasa",
            price: '26,00€',
          },
        ],
        meat: [
          { num: '24', name: 'Entrecot Madurado Iruki', price: '26,00€' },
          {
            num: '25',
            name: 'Solomillo de Ternera Iruki',
            price: '28,00€',
            recommended: true,
          },
          {
            num: '26',
            name: 'Secreto Ibérico al Teriyaki',
            desc: 'Amb patates palla',
            price: '21,00€',
          },
          { num: '27', name: 'Pluma Ibérica a la Plancha', price: '26,00€' },
          {
            num: '28',
            name: 'Entraña de Cerdo Ibérico',
            desc: 'A la brasa o crema Astillero',
            price: '26,00€',
          },
          {
            num: '29',
            name: 'Chuletón Maduro 500g',
            desc: 'Amb patates palla i verdures',
            price: '38,00€',
            recommended: true,
          },
        ],
        rice: [
          { num: '97', name: 'Arroz Marinera con Gamba Roja', price: '21,00€' },
          { num: '98', name: 'Arroz Negro', price: '21,00€' },
          {
            num: '99',
            name: 'Arroz Caldoso con Bogavante',
            desc: 'Gratinat amb allioli (Nacional i viu)',
            price: '33,00€',
            recommended: true,
          },
          { num: '100', name: 'Fideuà', price: '19,50€' },
        ],
        kids: [
          {
            num: '18',
            name: 'Pasta Fresca',
            desc: 'Carbonara o bolonyesa',
            price: '13,50€',
          },
          { num: '19', name: 'Canelones', price: '10,50€' },
          { num: '20', name: 'Bandidos con Patatas', price: '13,50€' },
          { num: '21', name: 'Butifarra con Patatas', price: '13,50€' },
          {
            num: '22',
            name: 'Croquetas de Jamón con Patatas',
            price: '12,50€',
          },
        ],
        desserts: [
          { num: '106', name: 'Cardenal', price: '10,50€' },
          { num: '107', name: 'Tarta de Queso', price: '8,80€' },
          {
            num: '108',
            name: 'Torrijas',
            desc: "Amb Xarrup d'Arròs i Gelat de Vainilla",
            price: '8,80€',
          },
          {
            num: '109',
            name: 'Hojaldre de Piñones',
            desc: 'Amb crema catalana',
            price: '8,80€',
          },
          {
            num: '110',
            name: 'Brookies',
            desc: 'Amb gelat de vainilla',
            price: '8,80€',
          },
          {
            num: '111',
            name: 'Coulant de Chocolate',
            desc: 'Amb Gelat de Vainilla',
            price: '7,00€',
          },
          { num: '112', name: 'Trufas Heladas con Nata', price: '7,50€' },
          { num: '113', name: 'Fresas con Nata', price: '8,80€' },
          {
            num: '114',
            name: 'Sorbete de Mandarina',
            desc: 'Amb Coulis de fruites del bosc',
            price: '8,50€',
          },
          {
            num: '115',
            name: 'Mosaico de Frutas del Tiempo',
            desc: 'Amb xocolata calenta',
            price: '12,50€',
          },
        ],
        extras: [
          { num: '123', name: 'Pan', price: '1,30€' },
          { num: '124', name: 'Salsas', price: '2,50€' },
          { num: '125', name: 'Pan con Tomate', price: '2,00€' },
        ],
        dinnerStarters: [
          {
            num: '210',
            name: 'Ensalada de Tomate y Burrata',
            desc: 'Amb gamba i tòfona',
            price: '16,50€',
          },
          {
            num: '211',
            name: 'Ensalada de Brotes Frescos',
            desc: 'Amb tàrtar de Tonyina',
            price: '16,50€',
          },
          {
            num: '212',
            name: 'Carpaccio de Gamba Roja',
            desc: 'Amb maionesa de wasabi i brots frescos',
            price: '21,00€',
          },
          {
            num: '213',
            name: 'Carpaccio de Pulpo de Roca',
            desc: 'Estil Gallec',
            price: '21,00€',
          },
          {
            num: '214',
            name: 'Jamón 100% Bellota 100g',
            desc: 'Amb Pa i Tomàquet',
            price: '26,00€',
          },
          {
            num: '215',
            name: 'Tabla de Quesos',
            desc: 'Amb Melmelada i Fruits Secs',
            price: '16,50€',
          },
        ],
        dinnerGrill: [
          {
            num: '4',
            name: 'Alcachofas de Temporada a la Brasa',
            price: '5,00€',
            pricePerUnit: true,
          },
          {
            num: '27',
            name: 'Rodaballo de Lonja',
            desc: 'Per compartir, acompanyat de verduretes',
            price: '86,00€',
          },
          {
            num: '216',
            name: 'Lomo Alto de Bacalao a la Brasa',
            desc: 'Amb verdures',
            price: '28,00€',
            pricePerUnit: true,
          },
          {
            num: '29',
            name: 'Chuletón Maduro 500g',
            desc: 'Amb patates palla i verdures',
            price: '38,00€',
          },
          {
            num: '217',
            name: 'Galta al Horno',
            desc: 'Estil el Astillero',
            price: '17,50€',
          },
          {
            num: '28',
            name: 'Entraña de Cerdo Ibérico',
            desc: 'A la brasa o crema Astillero',
            price: '26,00€',
          },
          { num: '124', name: 'Salsas', price: '2,50€' },
          { num: '125', name: 'Pan con Tomate', price: '2,00€' },
        ],
        dinnerVermut: [
          { num: '122', name: 'Aceitunas', price: '2,00€' },
          { num: '218', name: 'Bolsa Patatas Chips', price: '2,00€' },
          { num: '219', name: 'Patatas Fritas', price: '5,00€' },
        ],
      },
    },
    reservations: {
      title: 'Fes la teva Reserva',
      subtitle: "T'esperem",
      form: {
        name: 'Nom complet',
        email: 'Correu electrònic',
        phone: 'Telèfon',
        date: 'Data',
        time: 'Hora',
        guests: 'Nombre de persones',
        requests: 'Peticions especials (opcional)',
        submit: 'Confirmar Reserva',
        successTitle: 'Reserva sol·licitada!',
        successMessage:
          'Hem rebut la teva sol·licitud. Et contactarem aviat per confirmar la reserva.',
        newReservation: 'Fer una altra reserva',
      },
    },
    gallery: {
      title: 'Galeria',
      subtitle: 'La nostra essència en imatges',
    },
    location: {
      title: 'Ubicació i Contacte',
      subtitle: 'On trobar-nos',
      address: 'Adreça',
      phone: 'Telèfon',
      email: 'Email',
      hours: 'Horari',
      hoursText: 'Dim - Dium: 13:00 - 16:00, 20:00 - 23:00\nDilluns: Tancat',
      contactFormTitle: "Envia'ns un Missatge",
      contactFormSubtitle: 'Tens algun dubte?',
      formName: 'Nom',
      formEmail: 'Email',
      formMessage: 'Missatge',
      formSubmit: 'Enviar Missatge',
      formSuccess:
        'Missatge enviat correctament. Et respondrem el més aviat possible.',
    },
    pool: {
      title: 'Piscina & Lounge',
      subtitle: 'Relax mediterrani',
      description1:
        "Gaudeix de la nostra exclusiva piscina envoltada d'un entorn natural privilegiat. El complement perfecte per a una experiència gastronòmica inoblidable.",
      description2:
        "Relaxa't abans o després del teu àpat en un ambient que combina frescor, elegància i les millors vistes del Mediterrani. Un espai pensat per al gaudi i el descans.",
      featureTitle1: 'Piscina Privada',
      featureDesc1:
        "Refresca't a la nostra piscina amb vistes panoràmiques a l'entorn natural.",
      featureTitle2: 'Terrassa Lounge',
      featureDesc2:
        'Gaudeix de còctels i aperitius a la nostra terrassa al costat de la piscina.',
      featureTitle3: 'Capvespre Únic',
      featureDesc3:
        'Contempla els millors capvespres des de les nostres instal·lacions.',
      galleryTitle: "Descobreix l'Espai",
      cta: 'Reservar Experiència',
    },
    footer: {
      rights: 'Tots els drets reservats.',
      legal: 'Avís Legal',
      privacy: 'Política de Privacitat',
    },
  },
  en: {
    nav: {
      about: 'About',
      menu: 'Menu',
      pool: 'Pool & Lounge',
      reservations: 'Reservations',
      gallery: 'Gallery',
      contact: 'Contact',
      bookNow: 'Book a Table',
      home: 'Home',
    },
    hero: {
      tagline: 'Mediterranean tradition by the sea',
      book: 'Book a Table',
      menu: 'View Menu',
    },
    home: {
      discoverMore: 'Discover More',
      featuredTitle: 'Featured Dishes',
      featuredSubtitle: "Chef's Selection",
      viewFullMenu: 'View Full Menu',
      ctaTitle: 'Live the Experience',
      ctaSubtitle:
        'Book your table and enjoy authentic Mediterranean gastronomy by the sea.',
      viewGallery: 'View Full Gallery',
    },
    about: {
      title: 'Our Story',
      subtitle: 'Taste of the sea and tradition',
      description1:
        "Our story began on October 2, 2019, when we opened our doors for the first time in Sant Jordi d'Alfama with the dream of sharing our passion for seafood gastronomy.",
      description2:
        'However, the journey was not easy. Shortly after opening, a kitchen fire forced us to close. After much effort, we managed to reopen for a month, just before the global pandemic forced us to close again. Today, stronger than ever, we continue to offer the essence of the Mediterranean in every dish.',
      timelineTitle: 'A Path of Resilience',
      timeline: [
        {
          year: '2019',
          title: 'The Beginning',
          description:
            "On October 2, we opened our doors for the first time in Sant Jordi d'Alfama, full of hope and eager to share our cuisine.",
        },
        {
          year: '2019',
          title: 'The Fire',
          description:
            'Shortly after opening, a small kitchen fire damaged our facilities, preventing us from operating and forcing us to rebuild what we had just created.',
        },
        {
          year: '2020',
          title: 'Reopening & Pandemic',
          description:
            'After repairs, we managed to reopen and operated for about a month. However, in March 2020, the COVID-19 pandemic forced us to close again.',
        },
        {
          year: 'Today',
          title: 'Stronger Than Ever',
          description:
            'We persevered through adversity. Today we are open, stronger than ever, offering the authentic Mediterranean experience by the sea.',
        },
      ],
      philosophyTitle: 'Our Philosophy',
      philosophySubtitle: 'Our Pillars',
      phil1Title: 'Fresh Ingredients',
      phil1Desc:
        'We select the best local products daily at the local fish markets.',
      phil2Title: 'Mediterranean Tradition',
      phil2Desc:
        'We respect classic recipes while adding our personal and contemporary touch.',
      phil3Title: 'Warm Hospitality',
      phil3Desc:
        'We make you feel at home with attentive, professional, and friendly service.',
      teamTitle: 'The Team',
      teamSubtitle: 'Passion for cooking',
      teamDesc:
        'Behind every dish is a team of professionals passionate about gastronomy. Led by our Executive Chef, we work every day to offer you the best culinary experience, combining technique, creativity, and a deep respect for the product.',
    },
    menu: {
      title: 'Our Menu',
      subtitle: 'Authentic flavors',
      intro:
        'Discover our selection of dishes made with the best seasonal ingredients. A gastronomic proposal that pays homage to the flavors of the Mediterranean.',
      pricePerUnit: 'Price / unit',
      categories: {
        starters: 'Starters',
        fish: 'Fish',
        cooked: 'Cooked',
        meat: 'Meat',
        rice: 'Rice (min. 2 pax)',
        kids: 'Kids',
        desserts: 'Desserts',
        extras: 'Appetizers & Extras',
        dinnerStarters: 'Dinner - Starters',
        dinnerGrill: 'Dinner - Grill',
        dinnerVermut: 'Dinner - Vermouth',
      },
      items: {
        starters: [
          { num: '1', name: 'Ensalada Mediterránea con Atún', price: '11,50€' },
          {
            num: '2',
            name: 'Ensalada de Tomate de Montserrat',
            desc: 'Onion, Kalamata olives and Anchovies',
            price: '16,50€',
          },
          {
            num: '3',
            name: 'Alcachofas de Temporada a la Brasa',
            price: '5,00€',
            pricePerUnit: true,
          },
          {
            num: '4',
            name: 'Corazón de Alcachofas Confitadas',
            desc: 'With acorn-fed Iberian ham',
            price: '17,50€',
          },
          { num: '5', name: 'Mejillones al Estilo del Chef', price: '14,50€' },
          { num: '6', name: 'Mejillones a la Marinera', price: '16,50€' },
          { num: '7', name: 'Tallarinas', price: '21,00€' },
          { num: '8', name: 'Calamares a la Romana', price: '18,50€' },
          { num: '9', name: 'Fritada de Pescado', price: '18,50€' },
          {
            num: '10',
            name: 'Carpaccio de Pulpo de Roca',
            desc: 'Galician style',
            price: '21,00€',
            recommended: true,
          },
          {
            num: '11',
            name: 'Carpaccio de Gamba Roja',
            desc: 'With wasabi mayonnaise and fresh sprouts',
            price: '21,00€',
            recommended: true,
          },
          { num: '12', name: 'Croquetas de Setas', price: '16,50€' },
          {
            num: '13',
            name: 'Croquetón de Gambas',
            desc: 'With Truffle mayonnaise',
            price: '16,50€',
          },
          {
            num: '14',
            name: 'Croquetas de Cochinillo',
            desc: 'With Apple Heart',
            price: '16,50€',
          },
        ],
        fish: [
          {
            num: '77',
            name: 'Gamba Roja',
            price: '5,00€',
            pricePerUnit: true,
            recommended: true,
          },
          {
            num: '15',
            name: 'Cigalas 00',
            price: '13,50€',
            pricePerUnit: true,
            recommended: true,
          },
          {
            num: '16',
            name: 'Langostinos de Lonja',
            desc: '8 units',
            price: '28,00€',
          },
          { num: '17', name: 'Dorada a la Plancha', price: '21,00€' },
          {
            num: '18',
            name: 'Lenguado de Lonja a la Plancha',
            price: '28,00€',
          },
          {
            num: '19',
            name: 'Rape de Profundidad a la Plancha',
            price: '28,00€',
          },
          { num: '20', name: 'Suquet de Rape', price: '31,00€' },
          {
            num: '21',
            name: 'Calamares de Costa a la Plancha',
            price: '21,00€',
          },
          {
            num: '22',
            name: 'Lomo de Bacalao Gratinado',
            desc: 'With Garlic Mousseline',
            price: '26,00€',
          },
          {
            num: '23',
            name: 'Pata de Pulpo Mediterráneo 250g',
            desc: 'Grilled with Aioli and Confit Potato',
            price: '22,00€',
          },
          {
            num: '24',
            name: 'Lomo de Atún Balfegó a la Plancha',
            price: '28,00€',
            recommended: true,
          },
          {
            num: '25',
            name: 'Tataki de Atún BALFEGÓ',
            price: '26,00€',
            recommended: true,
          },
          {
            num: '26',
            name: 'Sashimi de Atún BALFEGÓ',
            price: '26,00€',
            recommended: true,
          },
          {
            num: '27',
            name: 'Rodaballo de Lonja',
            desc: 'To share, accompanied by vegetables',
            price: '86,00€',
            recommended: true,
          },
          {
            num: '28',
            name: 'Lata de Caviar 10g',
            desc: 'With Seaweed bread and butter',
            price: '38,00€',
            recommended: true,
          },
          {
            num: '29',
            name: 'Espardeñas 150g',
            price: '60,00€',
            recommended: true,
          },
        ],
        cooked: [
          {
            num: '205',
            name: 'Costilla de Vacuno a Baja Temperatura',
            desc: 'With mustard',
            price: '21,00€',
          },
          {
            num: '206',
            name: 'Costillas y Medianas de Cabrito',
            desc: 'From Horta de Sant Joan, Grilled',
            price: '26,00€',
          },
        ],
        meat: [
          { num: '24', name: 'Entrecot Madurado Iruki', price: '26,00€' },
          {
            num: '25',
            name: 'Solomillo de Ternera Iruki',
            price: '28,00€',
            recommended: true,
          },
          {
            num: '26',
            name: 'Secreto Ibérico al Teriyaki',
            desc: 'With straw potatoes',
            price: '21,00€',
          },
          { num: '27', name: 'Pluma Ibérica a la Plancha', price: '26,00€' },
          {
            num: '28',
            name: 'Entraña de Cerdo Ibérico',
            desc: 'Grilled or Astillero cream',
            price: '26,00€',
          },
          {
            num: '29',
            name: 'Chuletón Maduro 500g',
            desc: 'With straw potatoes and vegetables',
            price: '38,00€',
            recommended: true,
          },
        ],
        rice: [
          { num: '97', name: 'Arroz Marinera con Gamba Roja', price: '21,00€' },
          { num: '98', name: 'Arroz Negro', price: '21,00€' },
          {
            num: '99',
            name: 'Arroz Caldoso con Bogavante',
            desc: 'Gratin with aioli (National and alive)',
            price: '33,00€',
            recommended: true,
          },
          { num: '100', name: 'Fideuà', price: '19,50€' },
        ],
        kids: [
          {
            num: '18',
            name: 'Pasta Fresca',
            desc: 'Carbonara or bolognese',
            price: '13,50€',
          },
          { num: '19', name: 'Canelones', price: '10,50€' },
          { num: '20', name: 'Bandidos con Patatas', price: '13,50€' },
          { num: '21', name: 'Butifarra con Patatas', price: '13,50€' },
          {
            num: '22',
            name: 'Croquetas de Jamón con Patatas',
            price: '12,50€',
          },
        ],
        desserts: [
          { num: '106', name: 'Cardenal', price: '10,50€' },
          { num: '107', name: 'Tarta de Queso', price: '8,80€' },
          {
            num: '108',
            name: 'Torrijas',
            desc: 'With Rice Shot and Vanilla Ice Cream',
            price: '8,80€',
          },
          {
            num: '109',
            name: 'Hojaldre de Piñones',
            desc: 'With Catalan cream',
            price: '8,80€',
          },
          {
            num: '110',
            name: 'Brookies',
            desc: 'With vanilla ice cream',
            price: '8,80€',
          },
          {
            num: '111',
            name: 'Coulant de Chocolate',
            desc: 'With Vanilla Ice Cream',
            price: '7,00€',
          },
          { num: '112', name: 'Trufas Heladas con Nata', price: '7,50€' },
          { num: '113', name: 'Fresas con Nata', price: '8,80€' },
          {
            num: '114',
            name: 'Sorbete de Mandarina',
            desc: 'With wild berry Coulis',
            price: '8,50€',
          },
          {
            num: '115',
            name: 'Mosaico de Frutas del Tiempo',
            desc: 'With hot chocolate',
            price: '12,50€',
          },
        ],
        extras: [
          { num: '123', name: 'Pan', price: '1,30€' },
          { num: '124', name: 'Salsas', price: '2,50€' },
          { num: '125', name: 'Pan con Tomate', price: '2,00€' },
        ],
        dinnerStarters: [
          {
            num: '210',
            name: 'Ensalada de Tomate y Burrata',
            desc: 'With shrimp and truffle',
            price: '16,50€',
          },
          {
            num: '211',
            name: 'Ensalada de Brotes Frescos',
            desc: 'With Tuna tartare',
            price: '16,50€',
          },
          {
            num: '212',
            name: 'Carpaccio de Gamba Roja',
            desc: 'With wasabi mayonnaise and fresh sprouts',
            price: '21,00€',
          },
          {
            num: '213',
            name: 'Carpaccio de Pulpo de Roca',
            desc: 'Galician style',
            price: '21,00€',
          },
          {
            num: '214',
            name: 'Jamón 100% Bellota 100g',
            desc: 'With Bread and Tomato',
            price: '26,00€',
          },
          {
            num: '215',
            name: 'Tabla de Quesos',
            desc: 'With Jam and Nuts',
            price: '16,50€',
          },
        ],
        dinnerGrill: [
          {
            num: '4',
            name: 'Alcachofas de Temporada a la Brasa',
            price: '5,00€',
            pricePerUnit: true,
          },
          {
            num: '27',
            name: 'Rodaballo de Lonja',
            desc: 'To share, accompanied by vegetables',
            price: '86,00€',
          },
          {
            num: '216',
            name: 'Lomo Alto de Bacalao a la Brasa',
            desc: 'With vegetables',
            price: '28,00€',
            pricePerUnit: true,
          },
          {
            num: '29',
            name: 'Chuletón Maduro 500g',
            desc: 'With straw potatoes and vegetables',
            price: '38,00€',
          },
          {
            num: '217',
            name: 'Galta al Horno',
            desc: 'Astillero style',
            price: '17,50€',
          },
          {
            num: '28',
            name: 'Entraña de Cerdo Ibérico',
            desc: 'Grilled or Astillero cream',
            price: '26,00€',
          },
          { num: '124', name: 'Salsas', price: '2,50€' },
          { num: '125', name: 'Pan con Tomate', price: '2,00€' },
        ],
        dinnerVermut: [
          { num: '122', name: 'Aceitunas', price: '2,00€' },
          { num: '218', name: 'Bolsa Patatas Chips', price: '2,00€' },
          { num: '219', name: 'Patatas Fritas', price: '5,00€' },
        ],
      },
    },
    reservations: {
      title: 'Make a Reservation',
      subtitle: 'We are waiting for you',
      form: {
        name: 'Full Name',
        email: 'Email Address',
        phone: 'Phone Number',
        date: 'Date',
        time: 'Time',
        guests: 'Number of Guests',
        requests: 'Special Requests (optional)',
        submit: 'Confirm Reservation',
        successTitle: 'Reservation requested!',
        successMessage:
          'We have received your request. We will contact you soon to confirm the reservation.',
        newReservation: 'Make another reservation',
      },
    },
    gallery: {
      title: 'Gallery',
      subtitle: 'Our essence in images',
    },
    location: {
      title: 'Location & Contact',
      subtitle: 'Where to find us',
      address: 'Address',
      phone: 'Phone',
      email: 'Email',
      hours: 'Opening Hours',
      hoursText:
        'Tue - Sun: 1:00 PM - 4:00 PM, 8:00 PM - 11:00 PM\nMonday: Closed',
      contactFormTitle: 'Send us a Message',
      contactFormSubtitle: 'Have a question?',
      formName: 'Name',
      formEmail: 'Email',
      formMessage: 'Message',
      formSubmit: 'Send Message',
      formSuccess:
        'Message sent successfully. We will reply as soon as possible.',
    },
    pool: {
      title: 'Pool & Lounge',
      subtitle: 'Mediterranean Relaxation',
      description1:
        'Enjoy our exclusive pool surrounded by a privileged natural environment. The perfect complement for an unforgettable gastronomic experience.',
      description2:
        'Relax before or after your meal in an environment that combines freshness, elegance, and the best views of the Mediterranean. A space designed for enjoyment and rest.',
      featureTitle1: 'Private Pool',
      featureDesc1:
        'Cool off in our pool with panoramic views of the natural surroundings.',
      featureTitle2: 'Lounge Terrace',
      featureDesc2: 'Enjoy cocktails and appetizers on our poolside terrace.',
      featureTitle3: 'Unique Sunset',
      featureDesc3: 'Watch the best sunsets from our facilities.',
      galleryTitle: 'Discover the Space',
      cta: 'Book Experience',
    },
    footer: {
      rights: 'All rights reserved.',
      legal: 'Legal Notice',
      privacy: 'Privacy Policy',
    },
  },
}
